import { useState } from "react";
import { Link } from "react-router-dom";
import { BsArrowRight } from "react-icons/bs";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "./news.css";
import useFetchBlogs from "../../Hooks/useFetchBlogs";
import { BlogCardSkeleton } from "../../Components/BlogSkeleton";

// Helper function to format date to "Nov 14" style
const formatDate = (dateString) => {
  if (!dateString) return "Jan 1";
  try {
    const date = new Date(dateString);
    const month = date.toLocaleDateString("en-US", { month: "short" });
    const day = date.getDate();
    return `${month} ${day}`;
  } catch (e) {
    return "Jan 1";
  }
};

// Helper function to calculate read time
const calculateReadTime = (content) => {
  if (!content) return "3 min read";
  const wordsPerMinute = 200;
  const wordCount = content.split(/\s+/).length;
  const readTime = Math.ceil(wordCount / wordsPerMinute);
  return `${readTime} min read`;
};

// Helper function to truncate text
const truncateText = (text, maxLength) => {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + "...";
};

// Helper function to map blog data to content format
const mapBlogToContent = (blog) => {
  const fullTitle = blog.title || "Untitled";
  const fullContent = blog.excerpt || blog.description || blog.content || "";
  
  return {
    id: blog._id || blog.id,
    date: formatDate(blog.createdAt || blog.date || blog.publishedAt),
    min: blog.readTime || calculateReadTime(blog.content || blog.excerpt || blog.description),
    title: truncateText(fullTitle, 50), // Limit to ~50 chars like "U.S. Coast Guard Takes Over Cleanup of $3M Yacht"
    content: truncateText(fullContent, 200), // Limit to ~200 chars like the Lorem ipsum example
    img: blog.coverImage || blog.image || blog.featuredImage || blog.img || null,
    slug: blog.slug || null,
  };
};

// Helper function to map blog data to news item format
const mapBlogToNewsItem = (blog) => {
  const fullTitle = blog.title || "Untitled";
  const fullDescription = blog.excerpt || blog.description || blog.content || "";
  
  return {
    id: blog._id || blog.id,
    slug: blog.slug || null,
    date: formatDate(blog.createdAt || blog.date || blog.publishedAt),
    title: truncateText(fullTitle, 50), // Limit to ~50 chars
    description: truncateText(fullDescription, 200), // Limit to ~200 chars
    time: blog.readTime || calculateReadTime(blog.content || blog.excerpt || blog.description),
  };
};

const Newsinsignt = () => {
  const [latest, setLatest] = useState(true);
  const [popular, setPopular] = useState(false);
  const [displayCount, setDisplayCount] = useState(6);
  
  // Fetch blogs from API
  const { blogs, isLoading: blogsLoading } = useFetchBlogs();
  
  // Map blogs to content format for the card container - show based on displayCount
  const contents = blogs.length > 0 
    ? blogs.slice(0, displayCount).map(mapBlogToContent)
    : [];
  
  // Map blogs to news items format for the news list
  const newsItems = blogs.length > 0 
    ? blogs.map(mapBlogToNewsItem)
    : [];

  const handleLoadMore = () => {
    setDisplayCount(prev => prev + 6);
  };

  const hasMoreBlogs = blogs.length > displayCount;

  const handleLatest = () => {
    setLatest(true);
    setPopular(false);
  };

  const handlePopular = () => {
    setLatest(false);
    setPopular(true);
  };

  return (
    <div className="news">
      <div className="wrap">
      <div className="what">
        <h3>Latest News</h3>
        {blogsLoading ? (
          <div className="card-container">
            {[...Array(6)].map((_, index) => (
              <BlogCardSkeleton key={index} />
            ))}
          </div>
        ) : contents.length > 0 ? (
          <>
            <div className="card-container">
              {contents.map((item, index) => (
                <Link
                  key={item.id || index}
                  to={item.slug ? `/news/${item.slug}` : "#"}
                  className={`item-card ${index === 1 ? "highlight-card" : ""}`}
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <div className="meta">
                    <p>{item.date}</p>
                    <p>{item.min}</p>
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.content}</p>
                  {item.img && <img src={item.img} alt="" loading="lazy" />}
                  <div className="card-arrow">
                    <BsArrowRight />
                  </div>
                </Link>
              ))}
            </div>
            {hasMoreBlogs && (
              <button onClick={handleLoadMore} className="load-more-button">
                Load More
              </button>
            )}
          </>
        ) : (
          <div className="card-container">
            <div className="item-card">
              <div className="meta">
                <p>No news</p>
                <p>-</p>
              </div>
              <h3>No news available</h3>
              <p>Check back later for the latest updates.</p>
            </div>
          </div>
        )}
      </div>

      {/* Switch Tabs */}
      <div className="switch">
        <p onClick={handleLatest} className={latest ? "active" : ""}>
          Latest
        </p>
        <p onClick={handlePopular} className={popular ? "active" : ""}>
          Popular
        </p>
      </div>

      {latest && (
        <div className="content">
          {/* News List */}
          {blogsLoading ? (
            <div className="news-list">
              {[...Array(5)].map((_, index) => (
                <div key={index} className="news-card">
                  <div className="header">
                    <Skeleton width={80} height={14} />
                    <Skeleton width={70} height={14} />
                  </div>
                  <Skeleton height={20} style={{ marginBottom: "8px" }} />
                  <Skeleton count={2} height={16} />
                </div>
              ))}
            </div>
          ) : newsItems.length > 0 ? (
            <div className="news-list">
              {newsItems.map((item, index) => (
                <Link
                  key={item.id || index}
                  to={item.slug ? `/news/${item.slug}` : "#"}
                  className="news-card"
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <div className="header">
                    <p className="date">{item.date}</p>
                    <p className="time">{item.time}</p>
                  </div>
                  <h3 className="title">{item.title}</h3>
                  <p className="description">{item.description}</p>
                </Link>
              ))}
            </div>
          ) : (
            <div className="news-list">
              <div className="news-card">
                <div className="header">
                  <p className="date">No news</p>
                  <p className="time">-</p>
                </div>
                <h3 className="title">No news available</h3>
                <p className="description">Check back later for the latest updates.</p>
              </div>
            </div>
          )}

          {/* Ad Space */}
          <div className="ad-space">
            <p>Space for ads</p>
          </div>
        </div>
      )}

      {popular && (
        <div className="popular">
          <h3>Coming soon!</h3>
        </div>
      )}
       </div>
    </div>
  );
};

export default Newsinsignt;
